/**
 * Billing module — single source of truth for all bill-related API calls.
 * The legacy `api.bills` namespace is aliased to this same object in index.ts.
 */
import { fetchApi, API_BASE_URL, generateRequestId, ApiResponse } from '../client';
import { safeConsole } from '../../piiProtection';
import { buildQS } from '../helpers';

import type { PagedResponse } from '../../../types/pagination';

export interface BillingQueueParams {
  statFilter?: string;
  statuses?: string[];
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface BillingQueuePagedParams {
  page?: number;
  size?: number;
  /** A payment status, or one of the queue's pseudo-statuses PENDING · TODAY · OVERDUE · PAID (medopd-docs 136). */
  status?: string;
  startDate?: string;
  endDate?: string;
  patientId?: string;
  /** Bill number, doctor, patient name or mobile. */
  search?: string;
}

/** GET/PUT /api/bills/settings — the clinic's GST rate and overdue window (medopd-docs 136). */
export interface BillingSettings {
  gstPercent: number;
  overdueAfterDays: number;
}

/** POST /api/bills/quote — what the bill will come to, computed server-side the way the bill itself is. */
export interface BillQuote {
  subtotal: number;
  discountPercent: number;
  discountAmount: number;
  gstPercent: number;
  gstAmount: number;
  totalAmount: number;
}

export interface BillSummaryItem {
  id: string;
  billNumber: string;
  patientId: string;
  patientName: string;
  doctorId: string;
  doctorName: string;
  billDate: string;
  totalAmount: number;
  /** Collected so far; a part-paid bill shows it in the queue (medopd-docs 136). */
  paidAmount?: number;
  /** What the patient still owes. */
  amountDue?: number;
  /** Finalised, unpaid and older than the clinic's overdue window. */
  overdue?: boolean;
  paymentStatus: string;
  paymentMode: string | null;
  voided: boolean;
}

export const billingModule = {
  // ── Draft creation ─────────────────────────────────────────────────────────
  createBill: (billData: {
    visitId: string;
    patientId: string;
    doctorId?: string;
    clinicId?: string;
  }) =>
    fetchApi<any>('/api/bills', { method: 'POST', body: JSON.stringify(billData) }),

  // ── Read ───────────────────────────────────────────────────────────────────
  getBill: (billId: string) =>
    fetchApi<any>(`/api/bills/${billId}`),

  getById: (id: string) =>
    fetchApi<any>(`/api/bills/${id}`),

  // ── Items ──────────────────────────────────────────────────────────────────
  /** Replaces the draft's lines; the optional discount percent is stored on the bill and applied to its totals. */
  addItems: (billId: string, items: any[], discountPercent?: number) =>
    fetchApi<any>(`/api/bills/${billId}/items${buildQS({ discountPercent })}`, {
      method: 'PUT',
      body: JSON.stringify(items),
    }),

  updateBillItems: (billId: string, items: any[], discountPercent?: number) =>
    fetchApi<any>(`/api/bills/${billId}/items${buildQS({ discountPercent })}`, {
      method: 'PUT',
      body: JSON.stringify(items),
    }),

  // ── Clinic settings and quotes (medopd-docs 136) ───────────────────────────
  getSettings: () => fetchApi<BillingSettings>('/api/bills/settings'),

  updateSettings: (data: Partial<BillingSettings>) =>
    fetchApi<BillingSettings>('/api/bills/settings', { method: 'PUT', body: JSON.stringify(data) }),

  quote: (items: Array<{ itemName?: string; rate: number; quantity: number; amount?: number }>, discountPercent?: number) =>
    fetchApi<BillQuote>('/api/bills/quote', { method: 'POST', body: JSON.stringify({ items, discountPercent }) }),

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  finalizeBill: (billId: string) =>
    fetchApi<any>(`/api/bills/${billId}/finalize`, { method: 'POST' }),

  cancel: (billId: string) =>
    fetchApi<void>(`/api/bills/${billId}`, { method: 'DELETE' }),

  voidBill: (billId: string, reason: string) =>
    fetchApi<{ message: string; bill: any; status: string }>(`/api/bills/${billId}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  unvoidBill: (billId: string, reason: string) =>
    fetchApi<{ message: string; bill: any; status: string }>(`/api/bills/${billId}/unvoid`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // ── Payment ────────────────────────────────────────────────────────────────
  /**
   * Unified collectPayment — accepts either:
   *   collectPayment(billId, { amount, paymentMode, reference?, notes? })   ← object form
   *   collectPayment(billId, amount, paymentMode, reference?)               ← positional form
   */
  collectPayment: (
    billId: string,
    paymentOrAmount: any,
    paymentModeArg?: string,
    referenceArg?: string
  ) => {
    const body =
      typeof paymentOrAmount === 'number'
        ? { amount: paymentOrAmount, paymentMode: paymentModeArg, reference: referenceArg }
        : paymentOrAmount;
    return fetchApi<any>(`/api/bills/${billId}/payment`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  payBill: (
    billId: string,
    paymentData: { paymentStatus: 'PAID' | 'PENDING'; paidAmount: number; paymentDate: string }
  ) =>
    fetchApi<any>(`/api/bills/${billId}/pay`, {
      method: 'PATCH',
      body: JSON.stringify(paymentData),
    }),

  // ── Verification ───────────────────────────────────────────────────────────
  getVerificationToken: (billId: string) =>
    fetchApi<any>(`/api/bills/${billId}/verification-token`),

  // ── Queue ──────────────────────────────────────────────────────────────────
  getBillingQueue: (params?: BillingQueueParams) => {
    const p = new URLSearchParams();
    if (params?.statFilter) p.set('statFilter', params.statFilter);
    if (params?.statuses?.length) params.statuses.forEach(s => p.append('statuses', s));
    if (params?.dateFrom) p.set('dateFrom', params.dateFrom);
    if (params?.dateTo) p.set('dateTo', params.dateTo);
    if (params?.search) p.set('search', params.search);
    const qs = p.toString();
    return fetchApi<any>(`/api/bills/queue${qs ? '?' + qs : ''}`);
  },

  getBillingQueuePaged: (params?: BillingQueuePagedParams) =>
    fetchApi<PagedResponse<BillSummaryItem>>(`/api/billing${buildQS(params)}`),

  getBillingSummary: () =>
    fetchApi<Record<string, number>>('/api/bills/summary'),

  // ── Email ──────────────────────────────────────────────────────────────────
  sendEmail: (billId: string, email: string) =>
    fetchApi<{ message: string }>(`/api/bills/${billId}/send-email`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  /**
   * Send a finalised or paid bill to the patient: WhatsApp when the clinic has it connected, else SMS, else e-mail
   * (medopd-docs 136 round 2). {@code channel} says which one carried it.
   */
  sendToPatient: (billId: string) =>
    fetchApi<{ channel: 'WHATSAPP' | 'SMS' | 'EMAIL'; billNumber: string; message?: string }>(
      `/api/bills/${encodeURIComponent(billId)}/send-whatsapp`, { method: 'POST' }),

  // ── Legacy generate ────────────────────────────────────────────────────────
  generate: (billData: any) =>
    fetchApi<any>('/api/bills/generate', { method: 'POST', body: JSON.stringify(billData) }),

  // ── Revenue reporting ──────────────────────────────────────────────────────
  getRevenueSummary: () =>
    fetchApi<any>('/api/bills/revenue/summary'),

  getTodayRevenue: () =>
    fetchApi<any>('/api/bills/revenue/today'),

  getWeekRevenue: () =>
    fetchApi<any>('/api/bills/revenue/week'),

  getMonthRevenue: () =>
    fetchApi<any>('/api/bills/revenue/month'),

  getRevenueByDateRange: (startDate: string, endDate: string) =>
    fetchApi<any>(`/api/bills/revenue/range${buildQS({ startDate, endDate })}`),

  getPaymentMethodBreakdown: (startDate: string, endDate: string) =>
    fetchApi<Record<string, number>>(
      `/api/bills/revenue/methods${buildQS({ startDate, endDate })}`
    ),

  exportRevenueReport: async (
    startDate: string,
    endDate: string,
    format: string = 'csv'
  ): Promise<ApiResponse<{ blob: Blob; fileName: string }>> => {
    const headers: Record<string, string> = {
      'X-Request-ID': generateRequestId(),
      'X-Client-Version': '1.0.0',
    };

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/bills/revenue/export${buildQS({ startDate, endDate, format })}`,
        { method: 'GET', headers, credentials: 'include' } // cookie auth (GET → no CSRF needed)
      );
      if (!response.ok) return { error: 'Failed to export report' };

      const blob = await response.blob();
      const ext = format === 'pdf' ? 'pdf' : format === 'excel' ? 'xlsx' : 'csv';
      return { data: { blob, fileName: `revenue-report-${startDate}-${endDate}.${ext}` } };
    } catch (err) {
      safeConsole.error('Export failed:', err);
      return { error: 'Failed to export report' };
    }
  },
};
